import React, {
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  useEffect,
} from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { FormProvider, useForm } from 'react-hook-form';
import cn from 'classnames';
import { Url } from '@wix/da-url';
import {
  PapiDaSharedDeviationSubmitInit,
  PapiDeviation,
} from '@wix/da-papi-types';
import { MobileContext } from '@wix/da-react-context/pkg/MobileContext';
import { useAction } from '@wix/da-hooks/pkg/useAction';
import Modal from '@wix/da-ds/pkg/modals/dsPresets/Modal';
import ArrowLeftIcon from '@wix/da-ds/pkg/Icons/16x16/ArrowLeft';
import TextButton from '@wix/da-ds/pkg/Buttons/TextButton';
import BrandedButton from '@wix/da-ds/pkg/Buttons/BrandedButton';
import yupResolver from '@wix/da-shared-react/pkg/utils/yupResolver';
import { addErrorNotification } from '@wix/da-shared-react/pkg/Notifications/redux/actionCreators';
import { getCoreLevel } from '@wix/da-shared-react/pkg/publicSession/selectors';
import BuyCoreBrandedButton from '@wix/da-shared-react/pkg/Button/core/dsPresets/BuyCoreBrandedButton';
import SimpleTooltip from '@wix/da-ds/pkg/tooltips/dsPreset/SimpleTooltip';
import {
  BiData,
  BuyCoreButtonBiEvent,
  OriginalsSubmitClickBiEvent,
} from '@wix/da-bi/pkg/events';
import { useBiLogger } from '@wix/da-bi/pkg/hooks/useBiLogger';
import { ReferralType } from '@wix/da-bi/pkg/constants';
import FormStepsDisplay from './_partials/FormStepsDisplay';
import FirstStep from './_partials/FirstStep';
import SecondStep from './_partials/SecondStep';
import MultiImageUploader from '../../common/MultiImageUploader';
import {
  createOriginalDeviation,
  updateOriginalDeviation,
  publishOriginalDeviation,
} from '../../redux/actions/originals';
import { splashReady } from '../../redux/actions/modal';
import { OriginalDeviationFormFields } from './types';
import { useFirstStepValidation } from './validation';
import { useDefaultValues } from './hooks/useDefaultValues';

import s from './OriginalDeviationPopup.scss';

const FINAL_STEP = 2;
const BETTER_THAN_BASIC_CORE: ReturnType<typeof getCoreLevel>[] = [
  'core_plus',
  'core_pro',
  'core_pro_plus',
  'core_max',
];

interface Props {
  initResponse?: PapiDaSharedDeviationSubmitInit;
  onRequestClose: () => void;
}

const OriginalDeviationPopup: React.FC<Props> = ({
  initResponse,
  onRequestClose,
}) => {
  const { t } = useTranslation();
  const deviationFromProps = initResponse?.deviation;
  const [originalDeviation, setOriginalDeviation] = useState<
    PapiDeviation | undefined
  >(deviationFromProps);
  const deviationIdRef = useRef<number>(undefined);
  const scrollContainerRef = useRef<HTMLDivElement | null>(null);
  const [step, setStep] = useState(1);
  const [fileMissing, setFileMissing] = useState<boolean>(false);
  const [questionsMissing, setQuestionsMissing] = useState<boolean>(false);
  const [isSavingDraft, setIsSavingDraft] = useState<boolean>(false);

  const isMobile = useContext(MobileContext);
  const logEvent = useBiLogger();
  const firstStepValidationSchema = useFirstStepValidation();

  const dispatchCreateOriginalDeviation = useAction(createOriginalDeviation);
  const dispatchUpdateOriginalDeviation = useAction(updateOriginalDeviation);
  const dispatchPublishOriginalDeviation = useAction(publishOriginalDeviation);
  const dispatchAddErrorNotification = useAction(addErrorNotification);
  const dispatchSplashReady = useAction(splashReady);

  const resolver = useMemo(
    () =>
      step === 1
        ? yupResolver<OriginalDeviationFormFields>(firstStepValidationSchema, {
            abortEarly: false,
          })
        : undefined,
    [step, firstStepValidationSchema]
  );

  const defaultValues = useDefaultValues(initResponse);
  const formContext = useForm<OriginalDeviationFormFields>({
    mode: 'onSubmit',
    resolver,
    shouldUnregister: false,
    defaultValues,
  });

  const { trigger, getValues, formState } = formContext;

  const scrollToFirstError = useCallback(() => {
    const errors = formState.errors;
    if (!errors || !scrollContainerRef.current) {
      return;
    }
    const firstKey = Object.keys(errors)[0];
    if (!firstKey) {
      return;
    }
    const el = scrollContainerRef.current.querySelector(
      `[data-field="${firstKey}"]`
    );
    el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, [formState.errors]);

  const handleContinueClick = useCallback(async () => {
    if (step === 1) {
      const isValid = await trigger();
      const hasError =
        !isValid || !originalDeviation || originalDeviation.isDefaultImage;

      logEvent(
        BiData<OriginalsSubmitClickBiEvent>({
          evid: 760,
          is_error: hasError ? 1 : 0,
          step_num: 1,
        })
      );

      if (!originalDeviation || originalDeviation.isDefaultImage) {
        setFileMissing(true);
        return;
      }
      if (!isValid) {
        scrollToFirstError();
        return;
      }

      setFileMissing(false);
      setStep(step + 1);
    }
  }, [step, trigger, originalDeviation, scrollToFirstError, logEvent]);

  const redirectToDeviation = useCallback((deviationId: number) => {
    window.location.assign(Url.deviationLink(deviationId));
  }, []);

  const onPublishOriginalDeviation = useCallback(
    (updatedDeviation: PapiDeviation) =>
      dispatchPublishOriginalDeviation({
        deviationId: updatedDeviation.deviationId,
        onSuccess: () => {
          redirectToDeviation(updatedDeviation.deviationId);
        },
      }),
    [dispatchPublishOriginalDeviation, redirectToDeviation]
  );

  const handleSubmitClick = useCallback(() => {
    const formData = getValues();
    setQuestionsMissing(false);

    const hasError = !originalDeviation || !(formData.questions || []).length;

    logEvent(
      BiData<OriginalsSubmitClickBiEvent>({
        evid: 760,
        is_error: hasError ? 1 : 0,
        step_num: 2,
      })
    );

    if (!originalDeviation) {
      dispatchAddErrorNotification(
        t('originalDeviationPopup.error.coverImage.required')
      );
    } else if (!(formData.questions || []).length) {
      setQuestionsMissing(true);
    } else {
      dispatchUpdateOriginalDeviation({
        deviationId: originalDeviation.deviationId,
        formData,
        onSuccess: (updatedDeviation: PapiDeviation) => {
          if (!updatedDeviation.isPublished) {
            onPublishOriginalDeviation(updatedDeviation);
          } else {
            redirectToDeviation(updatedDeviation.deviationId);
          }
        },
      });
    }
  }, [
    t,
    getValues,
    originalDeviation,
    onPublishOriginalDeviation,
    dispatchUpdateOriginalDeviation,
    dispatchAddErrorNotification,
    redirectToDeviation,
    logEvent,
  ]);

  const handleSaveDraftClick = useCallback(() => {
    const formData = getValues();
    setIsSavingDraft(true);

    if (!originalDeviation) {
      dispatchCreateOriginalDeviation({
        formData,
        onSuccess: (createdDeviation: PapiDeviation) => {
          deviationIdRef.current = createdDeviation.deviationId;
          setOriginalDeviation(createdDeviation);
          setIsSavingDraft(false);
          window.location.href = Url.studio({
            products: 'originals-unlisted',
          });
        },
        onError: () => {
          dispatchAddErrorNotification(t('somethingWentWrong'));
          setIsSavingDraft(false);
        },
      });
    } else {
      dispatchUpdateOriginalDeviation({
        deviationId: originalDeviation.deviationId,
        formData,
        onSuccess: (updatedDeviation: PapiDeviation) => {
          setOriginalDeviation(updatedDeviation);
          setIsSavingDraft(false);
          window.location.href = Url.studio({
            products: 'originals-unlisted',
          });
        },
        onError: () => {
          dispatchAddErrorNotification(t('somethingWentWrong'));
          setIsSavingDraft(false);
        },
      });
    }
  }, [
    t,
    getValues,
    originalDeviation,
    dispatchCreateOriginalDeviation,
    dispatchUpdateOriginalDeviation,
    dispatchAddErrorNotification,
  ]);

  // if deviation doesn't exist yet, create it before uploading the main image
  const handleBeforeUpload = useCallback(
    async () =>
      new Promise<boolean>((resolve, reject) => {
        if (!originalDeviation) {
          dispatchCreateOriginalDeviation({
            formData: {} as OriginalDeviationFormFields,
            onSuccess: (createdDeviation: PapiDeviation) => {
              // it is key to use a ref here to ensure the deviationId is set before the upload starts
              deviationIdRef.current = createdDeviation.deviationId;
              setOriginalDeviation(createdDeviation);
              resolve(true);
            },
            onError: (error: any) => {
              reject(error);
            },
          });
          return;
        }
        resolve(true);
      }),
    [originalDeviation, dispatchCreateOriginalDeviation]
  );

  // The deviation is changed by adding a preview
  const handleAfterUpload = useCallback(deviation => {
    setOriginalDeviation(deviation);
  }, []);

  useEffect(() => {
    dispatchSplashReady();
  }, [dispatchSplashReady]);

  useEffect(() => {
    scrollContainerRef.current?.scrollTo({ top: 0 });
  }, [step]);

  // restrict submitting to a level better than core_access
  const submitButton = (
    <BrandedButton
      key="confirm"
      variant="brand-green"
      size="large"
      onClick={handleSubmitClick}
    >
      {deviationFromProps ? t('common.update') : t('common.submit')}
    </BrandedButton>
  );
  const upgradeButton = (
    <SimpleTooltip text={t('originalDeviationPopup.upgrade.tooltip')} usePortal>
      <BuyCoreBrandedButton
        size="medium"
        biData={BiData<BuyCoreButtonBiEvent>({
          evid: 375,
          referral_info: ReferralType.ORIGINAL_SUBMIT,
        })}
      >
        {t('originalDeviationPopup.upgrade')}
      </BuyCoreBrandedButton>
    </SimpleTooltip>
  );
  const coreLevel = useSelector(getCoreLevel);
  const finalButton = BETTER_THAN_BASIC_CORE.includes(coreLevel)
    ? submitButton
    : upgradeButton;

  return (
    <Modal
      isOpen
      onRequestClose={onRequestClose}
      className={s['popup']}
      contentClassName={s['contentWrapper']}
      heading={
        deviationFromProps
          ? t('originalDeviationPopup.heading.edit')
          : t('originalDeviationPopup.heading')
      }
      subheading={<FormStepsDisplay currentStep={step} />}
      withScrollableContentWrapper={isMobile}
      headerClassName={s['header']}
      showHeaderBorder
      showFooterBorder
      footerLeft={[
        step > 1 && (
          <TextButton
            key="cancel"
            variant="main"
            size="medium"
            icon={ArrowLeftIcon}
            onClick={() => setStep(step - 1)}
          >
            {t('common.back')}
          </TextButton>
        ),
      ]}
      footerRight={[
        !!originalDeviation?.isPublished && (
          <TextButton
            key="cancel"
            variant="main"
            size="large"
            onClick={onRequestClose}
          >
            {t('common.cancel')}
          </TextButton>
        ),
        !originalDeviation?.isPublished && (
          <TextButton
            key="draft"
            variant="main"
            size="large"
            disabled={isSavingDraft}
            onClick={handleSaveDraftClick}
          >
            {t('originalDeviationPopup.saveAsDraft')}
          </TextButton>
        ),
        step === FINAL_STEP ? (
          finalButton
        ) : (
          <BrandedButton
            key="confirm"
            variant="originals-angular"
            size="large"
            onClick={handleContinueClick}
          >
            {t('common.continue')}
          </BrandedButton>
        ),
      ]}
    >
      <FormProvider {...formContext}>
        <div className={cn(s['content'], s[`step-${step}`])}>
          <div className={s['main-area']} ref={scrollContainerRef}>
            {step === 1 && <FirstStep deviation={originalDeviation} />}
            {step === 2 && (
              <SecondStep
                scrollContainerRef={scrollContainerRef}
                deviation={originalDeviation}
                hasError={questionsMissing}
              />
            )}
          </div>
          <div className={s['preview-area']}>
            <MultiImageUploader
              isOriginals
              deviation={deviationFromProps}
              deviationId={deviationFromProps ? undefined : deviationIdRef}
              className={cn(
                originalDeviation &&
                  !originalDeviation.isDefaultImage &&
                  s['preview']
              )}
              emptyStateHasError={fileMissing}
              onBeforeUpload={handleBeforeUpload}
              onAfterUpload={handleAfterUpload}
            />
          </div>
        </div>
      </FormProvider>
    </Modal>
  );
};
OriginalDeviationPopup.displayName = 'OriginalDeviationPopup';

export default OriginalDeviationPopup;
